import { Component, effect, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Utilisateur } from '../../../../../data/intervenant/models/utilisateur.model';
import { AuthService } from '../../../../../core/services/auth/auth.service';
import { ButtonModule } from 'primeng/button';
import { NgClass, UpperCasePipe } from '@angular/common';
import { Menu } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NavigationContext, SessionContext } from 'src/app/core/services/config/app.settings';
import { UtilisateurTypeCode } from '../../../../../data/intervenant/models/enums/type-utilisateur.enum';
import { forkJoin, Subject, filter, takeUntil } from 'rxjs';
import { DomainesService } from '../../../../../data/intervenant/services/domaines/domaines.service';
import { OrganismesService } from '../../../../../data/intervenant/services/organismes/organismes.service';
import { UtilisateursService } from '../../../../../data/intervenant/services/utilisateurs/utilisateurs.service';
import { TranslateModule } from '@ngx-translate/core';
import { Operateur } from '../../../../../data/intervenant/models/operateur.model';
import { OperateursService } from '../../../../../data/intervenant/services/operateurs/operateurs.service';
import { CahiersService } from '../../../../../data/commission/services/cahiers/cahiers.service';

@Component({
    selector: 'app-header',
    standalone: true,
    imports: [ButtonModule, UpperCasePipe, Menu, NgClass, TranslateModule],
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit, OnDestroy {
    /**
     * True si l'application est l'ANPP
     */
    @Input() isAnpp: boolean;

    /**
     * True si l'utilisateur est admin
     */
    @Input() isAdmin: boolean;

    /**
     * Utilisateur
     */
    @Input() utilisateur: Utilisateur;

    /**
     * True si l'utilisateur peut changer de domaine / organisme
     */
    @Input() canChangeScope: boolean;

    /**
     * True si l'utilisateur peut modifier son mot de passe
     */
    @Input() canModifyPwd: boolean;

    /**
     * Items du menu
     */
    items: MenuItem[] = [];

    /**
     * Opérateur
     */
    operateur: Operateur;

    /**
     * Titre de la page pour l'i18n
     */
    pageTitleKey?: string;

    private syntheseLabel = 'Synthèse';

    private readonly cahiersService = inject(CahiersService);

    /**
     * Operateur service
     */
    private readonly operateursService = inject(OperateursService);

    /**
     * Auth service
     */
    private readonly authService = inject(AuthService);

    /**
     * Router
     */
    private readonly router = inject(Router);

    /**
     * Activated Route
     */
    private readonly route = inject(ActivatedRoute);

    /**
     * Domaines service
     */
    private readonly domainesService = inject(DomainesService);

    /**
     * Organismes service
     */
    private readonly organismesService = inject(OrganismesService);

    /**
     * Utilisateurs service
     */
    private readonly utilisateursService = inject(UtilisateursService);

    /**
     * Subscription observable
     */
    private readonly destroy$ = new Subject<void>();

    constructor() {
        effect(() => {
            const idOperateur = this.operateursService.idOperateur();
            if (idOperateur) {
                NavigationContext.set('idOperateur', idOperateur);
                this.loadOperateur(idOperateur);
            } else {
                NavigationContext.set('idOperateur', undefined);
                this.operateur = undefined;
            }
            this.loadSyntheseLabel();
        });
    }

    /**
     * On init component
     */
    ngOnInit() {
        this.loadData();
        this.refreshItems();
        this.loadSyntheseLabel();

        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.refreshPageTitle();
            });

        // initial
        this.refreshPageTitle();
    }

    /**
     * On destroy component
     */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadSyntheseLabel(): void {
        const idIntervenant = NavigationContext.has('idOperateur') ? NavigationContext.get('idOperateur') : SessionContext.get('idIntervenant');
        if (!idIntervenant) return;

        forkJoin([this.cahiersService.getUseTypeCahier(idIntervenant, 'AOC'), this.cahiersService.getUseTypeCahier(idIntervenant, 'IGP')])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([useAOC, useIGP]) => {
                if (useIGP && !useAOC) {
                    this.syntheseLabel = 'Produit IGP';
                } else if (useAOC && !useIGP) {
                    this.syntheseLabel = 'Produit AOC';
                } else {
                    this.syntheseLabel = 'Synthèse';
                }
                this.refreshPageTitle();
            });
    }

    /**
     * Rafraichir le titre de la page en fonction de la route actuelle.
     * @private
     */
    private refreshPageTitle(): void {
        const key = this.getActiveRouteTitleKey();
        this.pageTitleKey = key ?? undefined;
    }

    /**
     * Récupère le titre de la page actuelle
     * @private
     */
    private getActiveRouteTitleKey(): string | null {
        let current: ActivatedRoute | null = this.router.routerState.root;
        while (current?.firstChild) {
            current = current.firstChild;
        }

        const snapshot = current?.snapshot;
        if (!snapshot) {
            return null;
        }

        // 1) Angular route.title (si utilisé)
        const titleFromConfig = snapshot.routeConfig?.title;
        if (typeof titleFromConfig === 'string' && titleFromConfig.trim()) {
            return titleFromConfig;
        }

        const isSyntheseRoute = this.router.url.includes('/synthese');

        // 2) data.title (custom)
        const dataTitle = snapshot.data?.['title'];
        if (typeof dataTitle === 'string' && dataTitle.trim()) {
            return isSyntheseRoute ? this.syntheseLabel : dataTitle;
        }

        // 3) data.menu.libelle (clé i18n utilisée pour le menu)
        const menuLibelle = snapshot.data?.['menu']?.['libelle'];
        if (typeof menuLibelle === 'string' && menuLibelle.trim()) {
            return isSyntheseRoute ? this.syntheseLabel : this.normalizeMenuKey(menuLibelle);
        }

        return null;
    }

    /**
     * Normalise la clé du menu
     * @param key - Clé du menu
     * @private
     */
    private normalizeMenuKey(key: string): string {
        const domaine = SessionContext.get('codeDomaine');
        const prefix = domaine ? `menu.${domaine}.` : 'menu.';

        if (key.startsWith('menu.')) {
            return key.replace('menu.', prefix);
        }

        return key;
    }

    /**
     * Rafraichir les items du menu
     * @private
     */
    private refreshItems() {
        this.items = [];
        this.buildItems();
    }

    /**
     * Charge les données du composant
     * @private
     */
    private loadData() {
        if (window.location.pathname === '/main') {
            if (SessionContext.get('utilisateurType') === UtilisateurTypeCode.ADMIN) {
                this.router.navigate(['admin', 'organismes'], {
                    relativeTo: this.route
                });
            } else if (
                SessionContext.get('utilisateurType') === UtilisateurTypeCode.ORGANISME ||
                SessionContext.get('utilisateurType') === UtilisateurTypeCode.PI ||
                SessionContext.get('utilisateurType') === UtilisateurTypeCode.PO ||
                SessionContext.get('utilisateurType') === UtilisateurTypeCode.GP
            ) {
                this.router.navigate(['home'], { relativeTo: this.route });
            } else if (SessionContext.get('utilisateurType') === UtilisateurTypeCode.OC || SessionContext.get('utilisateurType') === UtilisateurTypeCode.CT) {
                this.router.navigate(['engagements/vergers-ecoresponsables'], {
                    relativeTo: this.route
                });
            } else {
                this.router.navigate(['operateurs/op/home'], {
                    relativeTo: this.route
                });
            }
        }
        if (SessionContext.get('utilisateurType') === UtilisateurTypeCode.ORGANISME || SessionContext.get('utilisateurType') === UtilisateurTypeCode.ADMIN) {
            forkJoin([this.domainesService.getDomaines(), this.organismesService.getOrganismes()]).subscribe(([domaines, organismes]) => {
                this.canChangeScope = domaines.length > 1 || organismes.length > 1;
                this.refreshItems();
            });
            this.canModifyPwd = true;
            this.refreshItems();

            this.utilisateursService.getUtilisateur(SessionContext.get('idIntervenant')).subscribe(utilisateur => {
                this.utilisateur = utilisateur;
                this.refreshItems();
            });
        } else if (
            SessionContext.get('utilisateurType') === UtilisateurTypeCode.PI ||
            SessionContext.get('utilisateurType') === UtilisateurTypeCode.GP ||
            SessionContext.get('utilisateurType') === UtilisateurTypeCode.PO ||
            SessionContext.get('utilisateurType') === UtilisateurTypeCode.CT ||
            SessionContext.get('utilisateurType') === UtilisateurTypeCode.OC
        ) {
            this.canModifyPwd = true;
            this.refreshItems();
            this.utilisateursService.getUtilisateur(SessionContext.get('idIntervenant')).subscribe(utilisateur => {
                this.utilisateur = utilisateur;
                this.refreshItems();
            });
        } else if (SessionContext.get('utilisateurType') === UtilisateurTypeCode.OPERATEUR) {
            this.canModifyPwd = true;
            this.refreshItems();
            forkJoin([this.utilisateursService.getUtilisateur(SessionContext.get('idIntervenant')), this.organismesService.getOrganismes()]).subscribe(
                ([utilisateur, organismes]) => {
                    this.utilisateur = utilisateur;
                    this.utilisateur.organismes = organismes;
                    this.refreshItems();
                }
            );
        }
    }

    /**
     * Build items
     */
    private buildItems() {
        if (!this.isAdmin && this.canChangeScope) {
            this.items.push({
                label: 'Changer de domaine / organisme',
                icon: 'pi pi-sync',
                command: () => this.changeScope()
            });
        }
        if (this.canModifyPwd && this.utilisateur) {
            this.items.push({
                label: 'Mon compte',
                icon: 'pi pi-user-edit',
                command: () => this.goToMonCompte()
            });
        }
        this.items.push({
            label: 'Se déconnecter',
            icon: 'pi pi-sign-out',
            command: () => this.logOut()
        });
    }

    /**
     * Se déconnecter
     */
    logOut() {
        this.operateursService.setIdOperateur(undefined);
        NavigationContext.set('idOperateur', undefined);
        this.authService.logOut();
    }

    /**
     * Redirection sur la page de modification du mot de passe
     */
    goToMonCompte() {
        this.router.navigate(['/main/mon-compte']);
    }

    /**
     * Changer de domaine / organisme
     */
    changeScope() {
        this.authService.changeScope();
    }

    /**
     * Charger les informations de l'opérateur en fonction de son id
     * @param idOperateur - L'id de l'opérateur
     */
    loadOperateur(idOperateur: number) {
        this.operateursService.getOperateur(idOperateur).subscribe(operateur => {
            this.operateur = operateur;
        });
    }

    /**
     * Changer d'opérateur
     */
    changeOperateur() {
        this.operateursService.setIdOperateur(undefined);
        NavigationContext.set('idOperateur', undefined);
        this.operateur = undefined;
        this.router.navigate(['/main/operateurs/or/recherche-affilie']);
    }
}
